import type { PasswordPolicy } from './api'

export type PasswordRuleKey = 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'symbol'

export interface PasswordRuleStatus {
  key: PasswordRuleKey
  met: boolean
  /** Only set for the `minLength` rule. */
  minLength?: number
}

/**
 * Evaluate a password against a policy for client-side UX hints. The backend is
 * always authoritative — this mirrors its rules so the user sees a live checklist
 * before submitting. Only rules the policy actually requires are returned.
 */
export function checkPasswordPolicy(
  password: string,
  policy: PasswordPolicy,
): { ok: boolean; rules: PasswordRuleStatus[] } {
  const rules: PasswordRuleStatus[] = [
    { key: 'minLength', met: password.length >= policy.minLength, minLength: policy.minLength },
  ]

  if (policy.requireUppercase) rules.push({ key: 'uppercase', met: /[A-Z]/.test(password) })
  if (policy.requireLowercase) rules.push({ key: 'lowercase', met: /[a-z]/.test(password) })
  if (policy.requireNumber) rules.push({ key: 'number', met: /[0-9]/.test(password) })
  if (policy.requireSymbol) rules.push({ key: 'symbol', met: /[^A-Za-z0-9]/.test(password) })

  return { ok: rules.every(rule => rule.met), rules }
}
